import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEmployeeInvoices, useUpdateInvoiceState } from '../hooks/useEmployeeInvoices';
import { StatusBadge } from '../components/workflow/StatusBadge';
import { Spinner } from '../components/common/Spinner';
import { formatMonthYear } from '../utils/dateFormat';
import { LEGACY_CZ_NUMBER_LOCALE, formatNumber } from '../utils/localeFormat';

export default function EmployeeInvoiceListPage() {
  const [filter, setFilter] = useState<'open' | 'sent' | 'all'>('open');
  const { data: invoices, isLoading } = useEmployeeInvoices();
  const updateStateMutation = useUpdateInvoiceState();

  const filtered = invoices
    ? filter === 'all'
      ? invoices
      : invoices.filter((i) => i.state === filter)
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">My Invoices</h1>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          {(['open', 'sent', 'all'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 ${filter === f ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              {f === 'all' ? 'All' : f === 'open' ? 'Open' : 'Sent'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      ) : filtered.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Billing</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Expenses</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((invoice) => {
                // MKS-351 — "October 2026" from the shared util.
                const monthName = formatMonthYear(new Date(invoice.year, invoice.month - 1));
                return (
                  <tr key={`${invoice.year}-${invoice.month}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link
                        to={`/my-invoices/${invoice.year}/${invoice.month}`}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600"
                      >
                        {monthName}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm text-gray-900">{invoice.totalHours.toFixed(1)}h</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm text-gray-900">{formatNumber(invoice.totalBilling, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm text-gray-900">{invoice.totalExpenses > 0 ? formatNumber(invoice.totalExpenses, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE) : '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm font-medium text-gray-900">{formatNumber(invoice.netAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)}</div>
                      {invoice.totalExtraCosts > 0 && (
                        <div className="text-xs text-red-500">-{formatNumber(invoice.totalExtraCosts, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, LEGACY_CZ_NUMBER_LOCALE)} deducted</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge state={invoice.state} size="sm" />
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {invoice.state === 'open' ? (
                        <button
                          onClick={() => updateStateMutation.mutate({ year: invoice.year, month: invoice.month, state: 'sent' })}
                          disabled={updateStateMutation.isPending}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Mark as Sent
                        </button>
                      ) : (
                        <button
                          onClick={() => updateStateMutation.mutate({ year: invoice.year, month: invoice.month, state: 'open' })}
                          disabled={updateStateMutation.isPending}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Revert to Open
                        </button>
                      )}
                      <Link
                        to={`/my-invoices/${invoice.year}/${invoice.month}`}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
          No {filter} invoices
        </div>
      )}
    </div>
  );
}
